import {
  DialogClose,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  toast,
} from '@qodo/design-system';
import { isAxiosError } from 'axios';
import { useChangePlanMutation } from 'network/mutations';
import { formatNumber } from 'utils/number';
import { getServerErrorDetail } from 'utils/serverError';
import { spawn } from 'utils/translations';
import { UpgradeConfirmationDialogProps } from './types';

const spawnDialog = spawn('dialogs.upgrade-confirmation');
const spawnDialogActions = spawn('dialogs.upgrade-confirmation.action');

/** Stripe declines a card at checkout with HTTP 402. */
const PAYMENT_REQUIRED = 402;

/**
 * Confirmation dialog for a self-serve in-place upgrade to a larger credit pack.
 * Unlike a downgrade (scheduled at period end), an upgrade applies immediately
 * with a prorated charge, so the primary CTA is emphasized and the copy explains
 * the immediate charge. On confirm the change-plan mutation fires; a card decline
 * (402) surfaces the payment-declined copy.
 *
 * When the current subscription is scheduled to cancel, the upgrade first voids
 * that cancellation, so the secondary label becomes "Keep cancellation" and a
 * re-activation note is shown.
 */
export const UpgradeConfirmationDialog = ({
  target,
  isScheduledToCancel = false,
  onConfirm,
  ...props
}: UpgradeConfirmationDialogProps) => {
  const { onOpenChange = () => {} } = props;
  const { mutate: changePlan, isPending } = useChangePlanMutation();

  const onSubmit = () => {
    // Guard re-entrancy: the button is loading-disabled while pending, but this
    // also blocks any non-UI re-invocation from firing a duplicate POST.
    if (!target || isPending) {
      return;
    }

    changePlan(
      { targetProductId: target.targetProductId, isUpgrade: true },
      {
        // Close only after the upgrade succeeds — an error keeps the dialog open
        // so the user sees the toast and can retry, rather than dismissing as if
        // the charge went through.
        onSuccess: () => {
          toast.success(spawnDialogActions('success'));
          onOpenChange(false);
          onConfirm?.();
        },
        onError: (error) => {
          const status = isAxiosError(error)
            ? error.response?.status
            : undefined;
          // A 402 is a card decline — fall back to payment-declined copy rather
          // than the generic retry copy when the BE sends no actionable detail.
          const fallbackKey =
            status === PAYMENT_REQUIRED ? 'declined' : 'error';

          toast.error(
            getServerErrorDetail(error) || spawnDialogActions(fallbackKey),
          );
        },
      },
    );
  };

  const cancelLabel = isScheduledToCancel
    ? spawnDialogActions('cancel-scheduled')
    : spawnDialogActions('cancel');

  return (
    <Dialog {...props}>
      <DialogContent
        autoFocus={false}
        className="sm:max-w-[425px]"
        // An upgrade is an immediate charge, so once the mutation is in flight
        // the dialog must not be dismissable — closing wouldn't cancel the POST,
        // and a later success would close the parent popup after the user thinks
        // they backed out. Block every dismissal path while pending.
        onEscapeKeyDown={(event) => {
          if (isPending) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (isPending) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-h3">{spawnDialog('title')}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {target
            ? spawnDialog('description', {
                credits: formatNumber(target.credits),
              })
            : null}
        </DialogDescription>
        {isScheduledToCancel && (
          <p className="text-sm text-text-panel-neutral-mid">
            {spawnDialog('reactivation-note')}
          </p>
        )}
        <DialogFooter>
          <Button variant="primary" onClick={onSubmit} loading={isPending}>
            {spawnDialogActions('confirm')}
          </Button>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              {cancelLabel}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
